import React from "react";
import PropTypes from "prop-types";
import {SelectDownArrow} from "../icons";

interface Item {
    id: number;
    value: string;
}

interface SelectBoxProps {
    items: Item[],
    defaultSelectedItemIndex?: number,
    selectedValues?: string[],
    placeholder?: string,
    label?: string,
    onChange: (items: Item[]) => void,
    className?: boolean
}

interface SelectBoxState {
    items: Item[];
    showItems: boolean;
    selectedItems: Item[];
    showError: boolean;
}

class MultiSelectDropdown extends React.Component<SelectBoxProps, SelectBoxState> {
    static propTypes = {
        items: PropTypes.array.isRequired,
        defaultSelectedItemIndex: PropTypes.number,
        selectedValues: PropTypes.array,
        placeholder: PropTypes.string,
        label: PropTypes.string,
        onChange: PropTypes.func.isRequired,
    };

    static defaultProps = {
        selectedValues: [],
        placeholder: "Select your use cases from drop-down",
    };

    private dropdownRef: React.RefObject<HTMLDivElement>;

    constructor(props: SelectBoxProps) {
        super(props);

        this.state = {
            items: props.items || [],
            showItems: false,
            selectedItems: this.getSelectedItems(props),
            showError: false,
        };

        this.dropdownRef = React.createRef();

        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        document.addEventListener("mousedown", this.handleClickOutside);

        const {items, defaultSelectedItemIndex} = this.props;
        if (defaultSelectedItemIndex !== undefined && items.length > 0) {
            const defaultSelectedItem = items[defaultSelectedItemIndex];
            this.setState({selectedItems: [defaultSelectedItem]}, () => {
                this.props.onChange([defaultSelectedItem]);
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutside);
    }

    handleClickOutside(event: MouseEvent) {
        if (this.state.showItems && this.dropdownRef.current && !this.dropdownRef.current.contains(event.target as Node)) {
            this.setState({showItems: false});
        }
    }

    getSelectedItems = (props: SelectBoxProps): Item[] => {
        const {items, selectedValues} = props;
        return selectedValues
            ? items.filter(item => selectedValues.includes(item.value))
            : [];
    };

    dropDown = () => {
        this.setState((prevState) => ({
            showItems: !prevState.showItems,
        }));
    };

    selectItem = (item: Item) => {
        this.setState(
            (prevState) => {
                const isAlreadySelected = prevState.selectedItems.some(
                    selectedItem => selectedItem.id === item.id
                );
                const selectedItems = isAlreadySelected
                    ? prevState.selectedItems.filter(
                        selectedItem => selectedItem.id !== item.id
                    )
                    : [...prevState.selectedItems, item];

                return {selectedItems, showItems: false, showError: false};
            },
            () => {
                this.props.onChange(this.state.selectedItems);
            }
        );
    };

    removeItem = (item: Item) => {
        this.setState(
            (prevState) => ({
                selectedItems: prevState.selectedItems.filter(
                    selectedItem => selectedItem.id !== item.id
                ),
            }),
            () => {
                this.props.onChange(this.state.selectedItems);
            }
        );
    };

    render() {
        const {placeholder} = this.props;
        const {className} = this.props;
        const {label} = this.props;
        const {selectedItems, showError} = this.state;

        return (
            <div
                className="select-box--box multi-dropdown"
                ref={this.dropdownRef}
            >
                <label>{label}</label>
                <div className={`select-box--container ${className ? "is-error contact-input" : "contact-input"}`} onClick={this.dropDown}>
                    <div className="select-box--selected-items">
                        {selectedItems.length === 0 ? (
                            <div className="placeholder-text">{placeholder}</div>
                        ) : (
                            selectedItems.map(item => (
                                <div key={item.id} className="selected-item">
                                    {item.value}
                                    <span
                                        className="remove-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.removeItem(item);
                                        }}
                                    >
                                        &times;
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="select-box--arrow">
                        <span
                            className={`${
                                this.state.showItems
                                    ? "select-box--arrow-up"
                                    : "select-box--arrow-down"
                            }`}
                        >
                            <SelectDownArrow/>
                        </span>
                    </div>

                    <div
                        style={{display: this.state.showItems ? "block" : "none"}}
                        className={"select-box--items"}
                    >
                        {this.state.items.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => this.selectItem(item)}
                                className={
                                    this.state.selectedItems.some(
                                        selectedItem => selectedItem.id === item.id
                                    )
                                        ? "selected"
                                        : ""
                                }
                            >
                                {item.value}
                            </div>
                        ))}
                    </div>
                </div>
                {showError && <div className="validation-error">Please select at least one use case.</div>}
            </div>
        );
    }
}

export default MultiSelectDropdown;
